import { workflowWorkspaceNavigationGuard } from './workflow-workspace/workflow-workspace-navigation.guard';
import { workflowStartNavigationGuard } from './workflow-start/workflow-start-navigation.guard';
import { votingTaskNavigationGuard } from './shared/workflow-voting/voting-task.guard';
import { Routes } from '@angular/router';
import { BreadcrumbRouteEntry } from './shared/components/masthead/breadcrumbs/breadcrumb/breadcrumb-items';
import { CaseContextRouteHostComponent } from './case-context/case-context-route-host.component';
import { HearingPhaseContentComponent, redirectSharedHearingView } from './phms/hearing-detail/hearing-phase-content.component';
import { HearingPhasePageComponent } from './phms/hearing-detail/hearing-phase-page.component';
import { hearingPreparationEntryGuard } from './phms/hearing-detail/hearing-preparation-entry.guard';
import { hearingPreparationNavigationGuard } from './phms/hearing-detail/hearing-preparation-navigation.guard';
import { registryDetailNavigationGuard } from './phms/registry-detail-navigation.guard';
import { HearingListComponent } from './phms/hearing-list/hearing-list.component';
import { HearingScheduleComponent } from './phms/hearing-schedule/hearing-schedule.component';
import { PostHearingTasksComponent } from './phms/post-hearing/post-hearing-tasks.component';
import { PlatformAdminTabsComponent } from './platform-admin/platform-admin-tabs.component';
import { platformAdminNavigationGuard } from './platform-admin/platform-admin-navigation.guard';
import { PhmsIntakeSubmissionDetailComponent } from './phms/intake-submissions/phms-intake-submission-detail.component';
import { PhmsIntakeSubmissionListComponent } from './phms/intake-submissions/phms-intake-submission-list.component';
import { PhmsManualIntakeSubmissionComponent } from './phms/intake/phms-manual-intake-submission.component';
import { PhmsIntakeStagingComponent } from './phms/intake/staging/phms-intake-staging.component';
import { PhmsIntakeReconciliationComponent } from './phms/intake/reconciliation/phms-intake-reconciliation.component';

// Breadcrumb entries that open a page other than the active route (see BreadcrumbRouteEntry).
const PHMS_CRUMB: BreadcrumbRouteEntry = { label: "PHMS", url: "/phms/dashboard" };
const INSTITUTION_MANAGEMENT_CRUMB: BreadcrumbRouteEntry = { label: "Institution Management", url: "/phms/facilities" };
const PARTICIPANTS_CRUMB: BreadcrumbRouteEntry = { label: "Participants", url: "/phms/participants" };
const INCARCERATED_PERSONS_CRUMB: BreadcrumbRouteEntry = { label: "Incarcerated Persons", url: "/phms/incarcerated-persons" };

export const FEATURE_ROUTES: Routes = [
    { path: "workflow-processes/:workflowCode/dashboards/:node/:dashboard", loadComponent: () => import('./workflow-dashboards/workflow-step-dashboard.component').then(m => m.WorkflowStepDashboardComponent), data: { breadcrumb: ["Step dashboard"], tabTitle: "Step dashboard", permissions: ["workflow_runtime.read"] } },
    { path: "workflow-processes/:workflowCode", loadComponent: () => import('./workflow-processes/workflow-process.component').then(m => m.WorkflowProcessComponent), data: { breadcrumb: ["Workflow overview"], tabTitle: "Workflow overview", permissions: ["workflow_runtime.read"] } },
    { path: "workflows/runs", loadComponent: () => import('./process-runs/process-runs.component').then(m => m.ProcessRunsComponent), data: { breadcrumb: ["Process runs"], tabTitle: "Process runs", permissions: ["workflow_runtime.read"] } },
    { path: "workflows/find", loadComponent: () => import('./shared/components/workflow-reference-locator/workflow-reference-finder-page.component').then(m => m.WorkflowReferenceFinderPageComponent), data: { breadcrumb: ["Find workflow reference"], tabTitle: "Find workflow reference", permissions: ["workflow_runtime.read"] } },
    { path: "workflows/start", canDeactivate: [workflowStartNavigationGuard], loadComponent: () => import('./workflow-start/workflow-start.component').then(m => m.WorkflowStartComponent), data: { breadcrumb: ["Start process"], tabTitle: "Start process", permissions: ["workflow_runtime.transition"] } },
    { path: "workflows/:workflow/voting/:occurrence", canDeactivate: [votingTaskNavigationGuard], loadComponent: () => import('./shared/workflow-voting/voting-task.component').then(m => m.VotingTaskComponent), data: {breadcrumb: ["Voting task"], tabTitle: "Voting task", permissions: ["workflow.voting.cast"]} },
    { path: "workflows/:workflow/steps/:occurrence", canDeactivate: [workflowWorkspaceNavigationGuard], runGuardsAndResolvers: 'always', loadComponent: () => import('./workflow-workspace/workflow-workspace.component').then(m => m.WorkflowWorkspaceComponent), data: { breadcrumb: ["Workflow workspace"], tabTitle: "Workflow workspace", permissions: ["workflow_runtime.read"] } },
    { path: "workflows/:workflow", canDeactivate: [workflowWorkspaceNavigationGuard], runGuardsAndResolvers: 'always', loadComponent: () => import('./workflow-workspace/workflow-workspace.component').then(m => m.WorkflowWorkspaceComponent), data: { breadcrumb: ["Workflow workspace"], tabTitle: "Workflow workspace", permissions: ["workflow_runtime.read"] } },
    {
        path: "workflow-dashboards/v2/:version/:node/:dashboard",
        loadComponent: () => import('./workflow-dashboards/workflow-dashboard.component').then(m => m.WorkflowDashboardComponent),
        data: { breadcrumb: ["Workflow worklist"], tabTitle: "Workflow worklist", permissions: ["workflow_runtime.read"], queueReadVersion: 2 },
    },
    {
        path: "workflow-dashboards/:version/:node/:dashboard",
        loadComponent: () => import('./workflow-dashboards/workflow-dashboard.component').then(m => m.WorkflowDashboardComponent),
        data: { breadcrumb: ["Workflow worklist"], tabTitle: "Workflow worklist", permissions: ["workflow_runtime.read"] },
    },
    {
        path: "platform/access",
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: {
            breadcrumb: ["Platform", "Navigation"],
            tabTitle: "Platform Navigation",
            permissions: ["platform_access_admin.read"],
            adminTabFragment: "access-navigation",
        },
    },
    {
        path: "platform-admin/roles-permissions",
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: {
            breadcrumb: ["Platform Admin", "Roles & Permissions"],
            tabTitle: "Platform Roles & Permissions",
            permissions: ["platform_access_admin.read"],
            adminTabFragment: "roles-permissions",
        },
    },
    {
        path: "platform-admin/user-access",
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: {
            breadcrumb: ["Platform Admin", "Access Management"],
            tabTitle: "Platform Access Management",
            permissions: ["platform_access_admin.read"],
            adminTabFragment: "user-access",
        },
    },
    {
        path: "platform-admin/scopes",
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: {
            breadcrumb: ["Platform Admin", "Scopes"],
            tabTitle: "Platform Scopes",
            permissions: ["platform_access_admin.read"],
            adminTabFragment: "scopes",
        },
    },
    {
        path: "platform-admin/feature-flags",
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: {
            breadcrumb: ["Platform Admin", "Feature Flags"],
            tabTitle: "Platform Feature Flags",
            permissions: ["platform_access_admin.read"],
            adminTabFragment: "feature-flags",
        },
    },
    {
        path: "platform-admin/lookup-metadata",
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: {
            breadcrumb: ["Platform Admin", "Lookup & Metadata"],
            tabTitle: "Platform Lookup & Metadata",
            permissions: ["platform_access_admin.read"],
            adminTabFragment: "lookup-metadata",
        },
    },
    {
        path: "platform-admin/terminology",
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: {
            breadcrumb: ["Platform Admin", "Terminology"],
            tabTitle: "Platform Terminology",
            permissions: ["terminology.read"],
            adminTabFragment: "terminology",
        },
    },
    {
        path: 'platform-admin/notice-fidelity',
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: { breadcrumb: ['Platform Admin', 'Notice fidelity'], tabTitle: 'Notice fidelity', permissions: ['content_template.read'], adminTabFragment: 'notice-fidelity' },
    },
    {
        path: 'platform-admin/document-assets',
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: { breadcrumb: ['Platform Admin', 'Document assets'], tabTitle: 'Document assets', permissions: ['content_template.read'], adminTabFragment: 'document-assets' },
    },
    {
        path: 'platform-admin/alert-templates',
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: { breadcrumb: ['Platform Admin', 'Alert templates'], tabTitle: 'Alert templates', permissions: ['content_template.read'], adminTabFragment: 'alert-templates' },
    },
    {
        path: "platform-admin/reason-catalogs",
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: { breadcrumb: ["Platform Admin", "Reason catalogs"], tabTitle: "Reason catalogs", permissions: ["platform_access_admin.read"], adminTabFragment: "reason-catalogs" },
    },
    {
        path: "platform-admin/configuration-diagnostics",
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: {
            breadcrumb: ["Platform Admin", "Configuration Diagnostics"],
            tabTitle: "Platform Configuration Diagnostics",
            permissions: ["platform_access_admin.read"],
            adminTabFragment: "configuration-diagnostics",
        },
    },
    {
        path: "platform-admin/workflow-registry-diagnostics",
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: {
            breadcrumb: ["Platform Admin", "Workflow Registry"],
            tabTitle: "Platform Workflow Registry",
            permissions: ["workflow_definition.read"],
            adminTabFragment: "workflow-registry-diagnostics",
        },
    },
    {
        path: "platform-admin/data-reset",
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: {
            breadcrumb: ["Platform Admin", "Data Reset"],
            tabTitle: "Data Reset",
            permissions: ["platform_access_admin.data_reset"],
            adminTabFragment: "data-reset",
        },
    },
    {
        path: "platform-admin/environment-setup",
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: {
            breadcrumb: ["Platform Admin", "Environment setup"],
            tabTitle: "Environment setup",
            permissions: ["platform_access_admin.seed"],
            adminTabFragment: "environment-setup",
        },
    },
    {
        path: "platform-admin/business-calendars",
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: {
            breadcrumb: ["Platform Admin", "Business calendars"],
            tabTitle: "Business calendars",
            permissions: ["platform_access_admin.read"],
            adminTabFragment: "business-calendars",
        },
    },
    {
        path: "platform-admin/operations",
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: {
            breadcrumb: ["Platform Admin", "Jobs & outbox"],
            tabTitle: "Jobs & outbox",
            permissions: ["platform_access_admin.read"],
            adminTabFragment: "operations",
        },
    },
    {
        path: "phms/admin",
        component: PlatformAdminTabsComponent,
        canDeactivate: [platformAdminNavigationGuard],
        data: {
            breadcrumb: ["PHMS", "Admin"],
            tabTitle: "PHMS Admin",
            permissions: ["platform_access_admin.read"],
            adminTabFragment: "user-access",
        },
    },
    {
        // #1503: the generic Home landing was removed. Each role now lands on its own role-scoped
        // dashboard (scheduler -> caseload, official -> their hearings, manager -> their queue), so
        // the old Home path redirects to that dashboard and keeps existing links/bookmarks working.
        path: "phms/home",
        pathMatch: "full",
        redirectTo: "phms/dashboard",
    },
    {
        path: "phms/dashboard",
        loadComponent: () =>
            import('./parole-dashboard/parole-dashboard.component').then((m) => m.ParoleDashboardComponent),
        data: {
            breadcrumb: ["PHMS", "Dashboard"],
            tabTitle: "PHMS Dashboard",
            permissions: ["workflow_runtime.read"],
        },
    },
    {
        path: "phms/alerts",
        loadComponent: () =>
            import('./workflow-alert-inbox/workflow-alert-inbox.component').then((m) => m.WorkflowAlertInboxComponent),
        data: {
            breadcrumb: ["PHMS", "Alerts"],
            tabTitle: "PHMS Alerts",
            permissions: ["workflow_runtime.read"],
        },
    },
    {
        path: "phms/reports",
        loadComponent: () =>
            import('./phms/reports/catalog/phms-report-catalog.component').then((m) => m.PhmsReportCatalogComponent),
        data: {
            breadcrumb: ["PHMS", "Reports"],
            tabTitle: "PHMS Reports",
            permissions: ["reports.read"],
        },
    },
    {
        path: "phms/reports/operational-workload",
        loadComponent: () =>
            import('./phms/reports/phms-reports.component').then((m) => m.PhmsReportsComponent),
        data: {
            breadcrumb: ["PHMS", "Reports", "Operational Workload"],
            tabTitle: "PHMS Operational Workload",
            permissions: ["reports.read"],
        },
    },
    {
        path: "phms/reports/workload-pipeline",
        loadComponent: () =>
            import('./phms/reports/workload-pipeline/phms-workload-pipeline-report.component').then(
                (m) => m.PhmsWorkloadPipelineReportComponent,
            ),
        data: {
            breadcrumb: ["PHMS", "Reports", "Workload Pipeline"],
            tabTitle: "PHMS Workload Pipeline",
            permissions: ["reports.read"],
        },
    },
    {
        path: "phms/reports/facility-stage-matrix",
        loadComponent: () =>
            import('./phms/reports/facility-stage-matrix/phms-facility-stage-matrix-report.component').then(
                (m) => m.PhmsFacilityStageMatrixReportComponent,
            ),
        data: {
            breadcrumb: ["PHMS", "Reports", "Facility Stage Matrix"],
            tabTitle: "PHMS Facility Stage Matrix",
            permissions: ["reports.read"],
        },
    },
    {
        path: "phms/reports/due-date-aging",
        loadComponent: () =>
            import('./phms/reports/due-date-aging/phms-due-date-aging-report.component').then(
                (m) => m.PhmsDueDateAgingReportComponent,
            ),
        data: {
            breadcrumb: ["PHMS", "Reports", "Due-Date Aging"],
            tabTitle: "PHMS Due-Date Aging",
            permissions: ["reports.read"],
        },
    },
    {
        path: "phms/reports/hearing-calendar-load",
        loadComponent: () =>
            import('./phms/reports/hearing-calendar-load/phms-calendar-load-report.component').then(
                (m) => m.PhmsCalendarLoadReportComponent,
            ),
        data: {
            breadcrumb: ["PHMS", "Reports", "Hearing Calendar Load"],
            tabTitle: "PHMS Hearing Calendar Load",
            permissions: ["reports.read"],
        },
    },
    {
        path: "phms/reports/eligibility-horizon",
        loadComponent: () =>
            import('./phms/reports/eligibility-horizon/phms-eligibility-horizon-report.component').then(
                (m) => m.PhmsEligibilityHorizonReportComponent,
            ),
        data: {
            breadcrumb: ["PHMS", "Reports", "Eligibility Horizon"],
            tabTitle: "PHMS Eligibility Horizon",
            permissions: ["reports.read"],
        },
    },
    {
        path: "phms/reports/caseload-by-owner",
        loadComponent: () =>
            import('./phms/reports/caseload-by-owner/phms-caseload-by-owner-report.component').then(
                (m) => m.PhmsCaseloadByOwnerReportComponent,
            ),
        data: {
            breadcrumb: ["PHMS", "Reports", "Caseload By Owner"],
            tabTitle: "PHMS Caseload By Owner",
            permissions: ["reports.read"],
        },
    },
    {
        path: "phms/reports/population-mix",
        loadComponent: () =>
            import('./phms/reports/population-mix/phms-population-mix-report.component').then(
                (m) => m.PhmsPopulationMixReportComponent,
            ),
        data: {
            breadcrumb: ["PHMS", "Reports", "Population Mix"],
            tabTitle: "PHMS Population Mix",
            permissions: ["reports.read"],
        },
    },
    {
        path: "phms/reports/age-profile",
        loadComponent: () =>
            import('./phms/reports/age-profile/phms-age-profile-report.component').then(
                (m) => m.PhmsAgeProfileReportComponent,
            ),
        data: {
            breadcrumb: ["PHMS", "Reports", "Age Profile"],
            tabTitle: "PHMS Age Profile",
            permissions: ["reports.read"],
        },
    },
    {
        path: "phms/reports/awaiting-hearing-input",
        loadComponent: () =>
            import('./phms/reports/awaiting-hearing-input/phms-awaiting-hearing-input-report.component').then(
                (m) => m.PhmsAwaitingHearingInputReportComponent,
            ),
        data: {
            breadcrumb: ["PHMS", "Reports", "Awaiting Hearing Input"],
            tabTitle: "PHMS Awaiting Hearing Input",
            permissions: ["reports.read"],
        },
    },
    {
        path: "phms/reports/alert-pressure",
        loadComponent: () =>
            import('./phms/reports/alert-pressure/phms-alert-pressure-report.component').then(
                (m) => m.PhmsAlertPressureReportComponent,
            ),
        data: {
            breadcrumb: ["PHMS", "Reports", "Alert Pressure"],
            tabTitle: "PHMS Alert Pressure",
            permissions: ["reports.read"],
        },
    },
    {
        path: "phms/intake",
        component: PhmsManualIntakeSubmissionComponent,
        data: {
            breadcrumb: ["PHMS", "Manual Intake Submission"],
            tabTitle: "PHMS Intake Submission",
            permissions: ["phms.intake.submit"],
        },
    },
    {
        path: "phms/intake/create-inmate",
        loadComponent: () =>
            import('./phms/intake/create-inmate/phms-create-inmate.component').then(
                (m) => m.PhmsCreateInmateComponent,
            ),
        data: {
            breadcrumb: ["PHMS", "Add Inmate"],
            tabTitle: "PHMS Add Inmate",
            permissions: ["phms.intake.submit"],
        },
    },
    {
        path: "phms/intake-submissions",
        component: PhmsIntakeSubmissionListComponent,
        data: {
            breadcrumb: ["PHMS", "Intake Submissions"],
            tabTitle: "PHMS Intake Submissions",
            permissions: ["phms.intake.submit"],
        },
    },
    {
        path: "phms/intake-submissions/staging",
        component: PhmsIntakeStagingComponent,
        data: {
            breadcrumb: ["PHMS", "Bulk Intake Staging"],
            tabTitle: "PHMS Bulk Intake Staging",
            permissions: ["phms.intake.submit"],
        },
    },
    {
        path: "phms/intake-submissions/reconciliation",
        component: PhmsIntakeReconciliationComponent,
        data: {
            breadcrumb: ["PHMS", "Interface Intake & Reconciliation"],
            tabTitle: "PHMS Interface Intake & Reconciliation",
            permissions: ["phms.intake.submit"],
        },
    },
    {
        path: "phms/intake-submissions/:id",
        component: PhmsIntakeSubmissionDetailComponent,
        data: {
            breadcrumb: ["PHMS", "Intake Submission Detail"],
            tabTitle: "PHMS Intake Submission Detail",
            permissions: ["phms.intake.submit"],
        },
    },
    {
        // Incarcerated persons (ADO #2174): the registry dashboard and the person detail page replace
        // the provisional ip-list; the old path redirects so bookmarks keep working.
        path: "phms/incarcerated-persons",
        loadComponent: () =>
            import('./phms/incarcerated-persons/incarcerated-persons-dashboard.component').then(
                (m) => m.IncarceratedPersonsDashboardComponent,
            ),
        data: {
            breadcrumb: [PHMS_CRUMB, "Incarcerated Persons"],
            tabTitle: "PHMS Incarcerated Persons",
            permissions: ["workflow_runtime.read"],
        },
    },
    {
        path: "phms/incarcerated-persons/:id",
        loadComponent: () =>
            import('./phms/incarcerated-persons/incarcerated-person-detail.component').then(
                (m) => m.IncarceratedPersonDetailComponent,
            ),
        data: {
            breadcrumb: [PHMS_CRUMB, INCARCERATED_PERSONS_CRUMB, "Incarcerated Person"],
            tabTitle: "PHMS Incarcerated Person",
            permissions: ["workflow_runtime.read"],
        },
    },
    {
        path: "phms/ip-list",
        redirectTo: "phms/incarcerated-persons",
        pathMatch: "full",
    },
    {
        path: "phms/hearing-list",
        component: HearingListComponent,
        data: {
            breadcrumb: ["PHMS", "Hearing List"],
            tabTitle: "PHMS Hearing List",
            permissions: ["workflow_runtime.read"],
        },
    },
    {
        path: "phms/hearing-schedule",
        component: HearingScheduleComponent,
        data: {
            breadcrumb: ["PHMS", "Hearing Schedule"],
            tabTitle: "PHMS Hearing Schedule",
            permissions: ["workflow_runtime.read"],
        },
    },
    {
        path: "phms/facilities",
        loadComponent: () =>
            import('./phms/facilities/facilities-dashboard.component').then(
                (m) => m.FacilitiesDashboardComponent,
            ),
        data: {
            breadcrumb: [PHMS_CRUMB, "Institution Management"],
            tabTitle: "PHMS Institution Management",
            permissions: ["workflow_runtime.read"],
        },
    },
    {
        path: "phms/facilities/:code",
        canDeactivate: [registryDetailNavigationGuard],
        runGuardsAndResolvers: "always",
        loadComponent: () =>
            import('./phms/facilities/facility-detail.component').then(
                (m) => m.FacilityDetailComponent,
            ),
        data: {
            breadcrumb: [PHMS_CRUMB, INSTITUTION_MANAGEMENT_CRUMB, "Institution"],
            tabTitle: "PHMS Institution",
            permissions: ["workflow_runtime.read"],
        },
    },
    {
        // The participant directory manages people and agencies independently of hearing attendance.
        path: "phms/participants",
        loadComponent: () =>
            import('./phms/participants/participants-dashboard.component').then(
                (m) => m.ParticipantsDashboardComponent,
            ),
        data: {
            breadcrumb: [PHMS_CRUMB, "Participants"],
            tabTitle: "PHMS Participants",
            permissions: ["workflow_runtime.read"],
        },
    },
    {
        path: "phms/participants/:id",
        canDeactivate: [registryDetailNavigationGuard],
        runGuardsAndResolvers: "always",
        loadComponent: () =>
            import('./phms/participants/participant-detail.component').then(
                (m) => m.ParticipantDetailComponent,
            ),
        data: {
            breadcrumb: [PHMS_CRUMB, PARTICIPANTS_CRUMB, "Participant"],
            tabTitle: "PHMS Participant",
            permissions: ["workflow_runtime.read"],
        },
    },
    {
        path: "phms/my-hearings",
        loadComponent: () =>
            import('./phms/my-hearings/my-hearings.component').then((m) => m.MyHearingsComponent),
        data: {
            breadcrumb: ["PHMS", "My Hearings"],
            tabTitle: "PHMS My Hearings",
            permissions: ["workflow_runtime.read"],
        },
    },
    {
        path: "phms/hearing-detail/:scheduledProceedingId",
        component: HearingPhasePageComponent,
        data: {
            breadcrumb: ["PHMS", "Hearing Cycle"],
            permissions: ["workflow_runtime.read"],
            // #1530: the phase pages render their own header; the app-wide workflow summary bar stays out.
            hearingHeader: true,
        },
        children: [
            {
                path: "",
                pathMatch: "full",
                redirectTo: "pre-hearing",
            },
            {
                path: "pre-hearing",
                component: HearingPhaseContentComponent,
                canActivate: [hearingPreparationEntryGuard],
                canDeactivate: [hearingPreparationNavigationGuard],
                runGuardsAndResolvers: 'always',
                data: {
                    breadcrumb: ["Pre-Hearing"],
                    tabTitle: "PHMS Hearing Cycle - Pre-Hearing",
                    permissions: ["workflow_runtime.read"],
                    phase: "pre",
                },
            },
            {
                path: "at-hearing",
                component: HearingPhaseContentComponent,
                data: {
                    breadcrumb: ["At-Hearing"],
                    tabTitle: "PHMS Hearing Cycle - At-Hearing",
                    permissions: ["workflow_runtime.read"],
                    phase: "at",
                },
            },
            {
                path: "post-hearing",
                component: HearingPhaseContentComponent,
                data: {
                    breadcrumb: ["Post-Hearing"],
                    tabTitle: "PHMS Hearing Cycle - Post-Hearing",
                    permissions: ["workflow_runtime.read"],
                    phase: "post",
                },
            },
            // #1530: the former shared destinations open the Pre-Hearing page with the matching tab
            // (`#sdmf`, `#documents`) or with Details & history expanded (`?details=open`).
            {
                path: "sdmf",
                canActivate: [redirectSharedHearingView("sdmf")],
                children: [],
                data: { permissions: ["workflow_runtime.read"], phase: "sdmf" },
            },
            {
                path: "documents",
                canActivate: [redirectSharedHearingView("documents")],
                children: [],
                data: { permissions: ["workflow_runtime.read"], phase: "documents" },
            },
            {
                path: "activity",
                canActivate: [redirectSharedHearingView("activity")],
                children: [],
                data: { permissions: ["workflow_runtime.read"], phase: "activity" },
            },
        ],
    },
    {
        path: "phms/post-hearing-tasks",
        component: PostHearingTasksComponent,
        data: {
            breadcrumb: ["PHMS", "Post-Hearing Tasks"],
            tabTitle: "PHMS Post-Hearing Tasks",
            permissions: ["workflow_runtime.read"],
        },
    },
    {
        path: "phms/legal-reviews",
        pathMatch: "full",
        redirectTo: "phms/dashboard?view=legal",
    },
    {
        path: "phms/legal-reviews/:decisionRecordId",
        loadComponent: () =>
            import('./phms/legal-reviews/legal-review-detail.component').then(
                (m) => m.LegalReviewDetailComponent,
            ),
        data: {
            breadcrumb: ["PHMS", "Legal Review", "Decision Review"],
            tabTitle: "PHMS Legal Decision Review",
            permissions: ["workflow_runtime.read"],
        },
    },
    {
        path: "case-context/inmate/:incarceratedPersonId",
        component: CaseContextRouteHostComponent,
        data: {
            breadcrumb: ["PHMS", "Case Context", "By Incarcerated Person"],
            tabTitle: "Inmate Case Context",
            permissions: ["inmate_offense_record.read"],
        },
    },
    {
        path: "case-context/proceeding/:scheduledProceedingId",
        component: CaseContextRouteHostComponent,
        data: {
            breadcrumb: ["PHMS", "Case Context", "By Scheduled Proceeding"],
            tabTitle: "Inmate Case Context",
            permissions: ["inmate_offense_record.read"],
        },
    },
];

